import Vue from 'vue';
import router from './router';
import store from './store';
import '@/assets/scss/reset.scss';
import '@/assets/scss/common.scss';
import '@/assets/scss/mixins.scss';
import GlobalMixin from './mixins/GlobalMixins';
import Detect from '@/plugin/Detect';
import ErrorHandler from './plugin/ErrorHandler';
import VueGtag from 'vue-gtag';
import VueGtm from '@gtm-support/vue2-gtm';
import vueMoment from 'vue-moment';
import VueCookies from 'vue-cookies';
import { enableWebSecurity } from './utils/security.util';
import * as Sentry from '@sentry/vue';
import { IGNORED_CLIENT_ERROR_CODES } from '@/constants/sentry.constants';

import App from './App.vue';
import { developmentSentryConfig } from '../sentry.client.development.config';
import { productionSentryConfig } from '../sentry.client.production.config';

Vue.mixin(GlobalMixin);

Vue.use(vueMoment);

const isDocplePlusSubdomain = () => {
  if (typeof window === 'undefined') return false;
  const { hostname } = window.location;
  return hostname.endsWith('.docple-plus.com') || hostname.endsWith('.dev-docple-plus.com');
};

Vue.use(VueCookies);

// Vue.use 후에 config로 다시 설정
if (isDocplePlusSubdomain()) {
  const domain = window.location.hostname.endsWith('.dev-docple-plus.com') ? '.dev-docple-plus.com' : '.docple-plus.com';
  VueCookies.config('7d', '/', domain);
} else {
  VueCookies.config('7d');
}

// 디바이스 체크
Vue.use(Detect);
store.dispatch('isMobile', Vue.$device.isMobile);
Vue.use(ErrorHandler);

Vue.config.productionTip = false;

if (process.env.NODE_ENV === 'production') {
  // 보안 설정 추가
  enableWebSecurity();

  Vue.use(VueGtm, {
    id: 'GTM-NG3GDC87',
    defer: false,
    compatibility: false,
    nonce: '3kd012l5hv8',
    enabled: true,
    debug: true,
    loadScript: true,
    vueRouter: router,
    trackOnNextTick: false,
  });
  Vue.use(
    VueGtag,
    {
      // GA 사업부 GA id : UA-27537265-1
      config: { id: 'G-Q1G0SRJQEX' },
      // 마케팅팀 요청시 아래로 변경
      // GA 마케팅팀 GA id UA-213374869-1
      // config: { id: 'UA-213374869-1' },
      params: {
        user_id: store.getters.gaTrackingId,
      },
    },
    router,
  );

  // 플러스 GTM
  Vue.use(VueGtm, {
    id: 'GTM-NW2FDFFP',
    defer: false,
    compatibility: false,
    enabled: true,
    debug: true,
    loadScript: true,
    vueRouter: router,
    trackOnNextTick: false,
  });

  // 플러스 GA
  Vue.use(
    VueGtag,
    {
      config: { id: 'G-GMD2GQGJC8' },
      params: {
        user_id: store.getters.gaTrackingId,
      },
    },
    router,
  );

  // 제니퍼 프론트 모니터링 상용일 때만
  (function(j, en, ni, fer) {
    j['dmndata'] = [];
    j['jenniferFront'] = function(args) {
      window.dmndata.push(args);
    };
    j['dmnaid'] = fer;
    j['dmnatime'] = new Date();
    j['dmnanocookie'] = false;
    j['dmnajennifer'] = 'JENNIFER_FRONT@INTG';
    var b = Math.floor(new Date().getTime() / 60000) * 60000;
    var a = en.createElement(ni);
    a.src = 'https://d-collect.jennifersoft.com/' + fer + '/demian.js?' + b;
    a.async = true;
    en.getElementsByTagName(ni)[0].parentNode.appendChild(a);
  })(window, document, 'script', '1ff976d7');
}

// 환경별 Sentry 설정 선택 (프로덕션 및 스테이징은 productionConfig, 개발 및 로컬에서는 developmentConfig를 사용)
const envConfig = process.env.VUE_APP_ENV === 'production' ? productionSentryConfig : developmentSentryConfig;

const sentryConfig = {
  Vue,
  dsn: process.env.VUE_APP_SENTRY_DSN,
  // 환경별 설정 적용 (enabled, debug, environment 등)
  ...envConfig,
  // *MEMO: 전역 에러인 GlobalHandler를 제외하는 것으로 전역 에러는 수집되지 않는다.
  // TODO(@jonathan.shin) - 차후 정상적으로 수동 캡쳐 되는지 확인 필요.
  integrations: defaultIntegrations => defaultIntegrations.filter(integration => integration.name !== 'GlobalHandlers'),
  // *MEMO: 수집 개수를 50으로 결정 -> 차후 에러 로그 보면서 조정 논의.
  maxBreadcrumbs: 50,
  // 크롬 확장 프로그램에서 발생하는 에러 방지
  denyUrls: [/extensions\//, /^chrome:\/\//],
  // 브라우저 자체 이슈, 코드 스플리팅, 의도된 중단, Promise rejection 등 방지.
  ignoreErrors: [
    'ResizeObserver loop limit exceeded',
    'Non-Error promise rejection captured',
    'Load failed',
    'AbortError',
    'The operation was aborted',
    'ChunkLoadError',
    /Loading chunk [\d]+ failed/,
    /Unexpected token '‹'/,
    /Unexpected token '<'/,
  ],
  // 메시지 캡처 시에도 스택 트레이스 자동 첨부
  attachStacktrace: true,
  // Object 데이터 확인 시, depth까지 진입할 것인지를 관리.
  normalizeDepth: 7,
  // 파싱된 HTML 문자열이나 base64 등의 큰 데이터 값을 제한하기 위해 반영.
  maxValueLength: 1000,
  tracesSampleRate: 0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,

  beforeBreadcrumb(breadcrumb) {
    // *MEMO: 콘솔로 전달하는 로그는 제외할 것.
    if (breadcrumb.category === 'console') {
      return null;
    }

    return breadcrumb;
  },

  // 에러 필터링
  beforeSend(event, hint) {
    // *MEMO: 민감 정보 중에 accessToken은 존재한다면 보내주도록 처리 (논의 완료)
    // *MEMO: - 여기선 닥플 리액트 로직 그대로 유지, 타 프로젝트는 accessToken 그대로 전달.
    const accessToken = Vue.$cookies?.get('accessToken');

    if (accessToken) {
      event.tags = {
        ...event.tags,
        accessToken: accessToken,
      };
    }
    // *MEMO: 400, 401, 403, 404, 409, 422는 필터링 처리하여 보내주지 않기로 한다.
    let statusCode;

    // 원본 에러 객체에서 status 확인
    if (hint.originalException && typeof hint.originalException === 'object' && hint.originalException.status) {
      statusCode = hint.originalException.status;
    }

    // HTTP 응답 컨텍스트에서 status_code 확인
    if (!statusCode && event.contexts?.response?.status_code) {
      statusCode = event.contexts.response.status_code;
    }

    // 무시할 에러 코드면 필터링
    if (statusCode && IGNORED_CLIENT_ERROR_CODES.includes(statusCode)) {
      return null;
    }

    return event;
  },
};

if (process.env.VUE_APP_SENTRY_RELEASE) {
  sentryConfig.release = process.env.VUE_APP_SENTRY_RELEASE;
}

Sentry.init(sentryConfig);

// eslint-disable-next-line no-unused-vars
Vue.config.errorHandler = (err, vm, info) => {
  if (Sentry.getCurrentHub().getClient()) {
    Sentry.captureException(err);
  }

  // 특정 오류 메시지나 조건에 따라 새로 고침
  if (isSpecificSyntaxError(err)) {
    console.error(err); // 오류 로그
    // 페이지를 새로 고침
    setTimeout(() => {
      location.reload();
    }, 3000); // 3초 후 새로 고침
  } else {
    console.warn('오류가 발생했습니다:', err);
  }
};

// 특정 오류 메시지를 체크하는 함수
function isSpecificSyntaxError(err) {
  return err.message && err.message.includes("Unexpected token '‹'");
}

function handleError(error) {
  console.log('handleError', error);
  const errorMessage = error?.message || error?.toString() || '';

  if (/loading chunk .* failed./i.test(errorMessage) || errorMessage.includes("Unexpected token '<'")) {
    console.error('에러 발생. 페이지를 새로고침합니다:', errorMessage);
    window.location.reload();
  }
}

window.onerror = function(message, source, lineno, colno, error) {
  handleError(error);
};

export default new Vue({
  router,
  store,
  render: h => h(App),
  errorHandler: error => {
    handleError(error);
  },
}).$mount('#app');
